import { useMemo, type ReactNode } from 'react';
import { useLiveRxQuery } from 'rxdb/plugins/react';
import { useStableQuery } from '../../db/useStableQuery';
import {
    AbilityProvider as CaslAbilityProvider,
    Can as CaslCan
} from '@casl/react';
import {
    defineAbilityFor,
    resolveAccessibleProjectIds
} from '@ast-data/shared';
import type { ProjectDocType, GroupDocType } from '@ast-data/shared';
import { useAuth } from './useAuth';
import { AbilityReadyContext } from './abilityContext';
import { useUserDirectory } from '../users/userDirectory';

/** `<Can I="update" a="Project">…</Can>` — guard JSX declaratively. */
export const Can = CaslCan;

/**
 * Builds the current user's ability from their synced roles + project memberships
 * and provides it to the tree. Reactive: changing a role/assignment re-derives it.
 */
export function AbilityProvider({ children }: { children: ReactNode }) {
    const { user } = useAuth();
    // The roles live on the profiles — read from the app's one user directory.
    const { profiles } = useUserDirectory();
    const projectsQuery = useStableQuery<ProjectDocType>({});
    const { results: projectsDocs } = useLiveRxQuery<ProjectDocType>({
        collection: 'projects',
        query: projectsQuery
    });
    const projects = useMemo(
        () => projectsDocs.map((doc) => doc.toMutableJSON() as ProjectDocType),
        [projectsDocs]
    );
    const groupsQuery = useStableQuery<GroupDocType>({});
    const { results: groupsDocs } = useLiveRxQuery<GroupDocType>({
        collection: 'groups',
        query: groupsQuery
    });
    const groups = useMemo(
        () => groupsDocs.map((doc) => doc.toMutableJSON() as GroupDocType),
        [groupsDocs]
    );

    const ability = useMemo(() => {
        const uid = user?.id;
        const roles = uid
            ? (profiles.find((profile) => profile.id === uid)?.roles ?? [])
            : [];
        // Membership is embedded on the project/group docs; the shared resolver
        // is the single source of truth (kept in sync with backend loadProjectIds).
        // The mapped JSON is DeepReadonly; cast to the plain doc type so
        // the embedded id arrays keep their string[] element type.
        const projectIds = uid
            ? resolveAccessibleProjectIds(
                  projects as readonly ProjectDocType[],
                  groups as readonly GroupDocType[],
                  uid
              )
            : [];
        return defineAbilityFor({ roles, projectIds });
    }, [user?.id, profiles, projects, groups]);

    /**
     * Any profile having arrived means the collection has replicated, so a user
     * with no profile of their own (they exist — not every account has one) is
     * role-less for real rather than still loading, and doesn't have to wait.
     * Derived from the same query that feeds the ability, so the two can never
     * disagree (a separate subscription could settle in a different tick).
     */
    const ready = profiles.length > 0;

    return (
        <CaslAbilityProvider value={ability}>
            <AbilityReadyContext.Provider value={ready}>
                {children}
            </AbilityReadyContext.Provider>
        </CaslAbilityProvider>
    );
}
